import React, {useMemo, useState} from 'react';
import {withTranslation} from 'react-i18next';
import {Trash} from '@phosphor-icons/react';
import {Switch} from '../buttons/Swtich';

const CHANNELS = [
    {value: 'mail', labelKey: 'alerts_configuration_builder.channel_mail', className: '-mail'},
    {value: 'whatsapp', labelKey: 'alerts_configuration_builder.channel_whatsapp', className: '-whatsapp'},
    {value: 'other', labelKey: 'alerts_configuration_builder.channel_other', className: '-other'},
];

const RECIPIENTS = [
    {value: 'me', labelKey: 'alerts_configuration_builder.recipient_me'},
    {value: 'client_users', labelKey: 'alerts_configuration_builder.recipient_client_users'},
];

const DIMENSIONS = [
    {value: 'client', labelKey: 'alerts_configuration_builder.dimension_client', sublist: 'clients'},
    {value: 'client_tag', labelKey: 'alerts_configuration_builder.dimension_client_tag', sublist: 'tags'},
    {value: 'any_client', labelKey: 'alerts_configuration_builder.dimension_any_client'},
    {value: 'control_line', labelKey: 'alerts_configuration_builder.dimension_control_line', sublist: 'controlLines'},
    {value: 'control_line_tag', labelKey: 'alerts_configuration_builder.dimension_control_line_tag', sublist: 'tags'},
];

const SUBLISTS = {
    clients: [
        {value: 'coca_cola', label: 'Coca-Cola'},
        {value: 'mercado_libre', label: 'Mercado Libre'},
        {value: 'nike', label: 'Nike'},
        {value: 'despegar', label: 'Despegar'},
        {value: 'farmacity', label: 'Farmacity'},
    ],
    tags: [
        {value: 'ecommerce', label: 'E-commerce'},
        {value: 'branding', label: 'Branding'},
        {value: 'high_priority', labelKey: 'alerts_configuration_builder.tag_high_priority'},
        {value: 'black_friday', label: 'Black Friday'},
        {value: 'retail', label: 'Retail'},
    ],
    controlLines: [
        {value: 'search_brand', label: 'Search - Marca'},
        {value: 'performance_max', label: 'Performance Max'},
        {value: 'meta_conversions', label: 'Meta - Conversiones'},
        {value: 'shopping', label: 'Shopping'},
        {value: 'tiktok_awareness', label: 'TikTok - Awareness'},
    ],
};

const ALERT_TYPES = [
    {value: 'critical_metric', labelKey: 'alerts_configuration_builder.type_critical_metric', metric: true},
    {value: 'url_down', labelKey: 'alerts_configuration_builder.type_url_down', metric: false},
    {value: 'account_without_impressions', labelKey: 'alerts_configuration_builder.type_account_without_impressions', metric: false},
];

const METRICS = [
    {value: 'roas', label: 'ROAS', unit: 'number'},
    {value: 'acos', label: 'ACOS', unit: 'percent'},
    {value: 'spend', labelKey: 'alerts_configuration_builder.metric_spend', unit: 'money'},
    {value: 'cpa', labelKey: 'alerts_configuration_builder.metric_cpa', unit: 'money'},
    {value: 'conversions', labelKey: 'alerts_configuration_builder.metric_conversions', unit: 'number'},
    {value: 'ctr', label: 'CTR', unit: 'percent'},
    {value: 'cpc', label: 'CPC', unit: 'money'},
    {value: 'cpm', label: 'CPM', unit: 'money'},
    {value: 'impressions', labelKey: 'alerts_configuration_builder.metric_impressions', unit: 'number'},
    {value: 'sales', labelKey: 'alerts_configuration_builder.metric_sales', unit: 'money'},
    {value: 'conversion_rate', labelKey: 'alerts_configuration_builder.metric_conversion_rate', unit: 'percent'},
];

const COMPARATORS = [
    {value: 'gt', labelKey: 'alerts_configuration_builder.comparator_gt'},
    {value: 'lt', labelKey: 'alerts_configuration_builder.comparator_lt'},
    {value: 'eq', labelKey: 'alerts_configuration_builder.comparator_eq'},
    {value: 'gte', labelKey: 'alerts_configuration_builder.comparator_gte'},
    {value: 'lte', labelKey: 'alerts_configuration_builder.comparator_lte'},
];

const INITIAL_ALERTS = [
    {
        id: 1,
        active: true,
        name: 'ROAS bajo - Coca-Cola',
        channel: 'mail',
        recipient: 'me',
        dimension: 'client',
        dimensionValue: 'coca_cola',
        type: 'critical_metric',
        metric: 'roas',
        comparator: 'lt',
        value: '2',
    },
    {
        id: 2,
        active: true,
        name: 'Caida de sitios',
        channel: 'whatsapp',
        recipient: 'client_users',
        dimension: 'any_client',
        dimensionValue: '',
        type: 'url_down',
        metric: 'roas',
        comparator: 'lt',
        value: '',
    },
    {
        id: 3,
        active: false,
        name: 'ACOS alto en Black Friday',
        channel: 'mail',
        recipient: 'client_users',
        dimension: 'control_line_tag',
        dimensionValue: 'black_friday',
        type: 'critical_metric',
        metric: 'acos',
        comparator: 'gt',
        value: '30',
    },
];

function findOption(options, value) {
    return options.find(option => option.value === value) || options[0];
}

function optionLabel(option, t) {
    if (!option) return '';
    return option.label || t(option.labelKey);
}

function SelectChip({value, options, onChange, placeholder, accent}) {
    return (
        <span className={`alert-builder-chip${!value ? ' -empty' : ''}${accent ? ' -accent' : ''}`}>
            <select value={value || ''} onChange={(e) => onChange(e.target.value)}>
                {placeholder && <option value="">{placeholder}</option>}
                {options.map(option => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
        </span>
    );
}

function ValueInput({metric, value, onChange}) {
    const metricConfig = findOption(METRICS, metric);
    const unit = metricConfig.unit;

    return (
        <span className="alert-builder-value">
            {unit === 'money' && <span>$</span>}
            <input
                type="text"
                inputMode="decimal"
                placeholder="0"
                value={value || ''}
                onChange={(e) => onChange(e.target.value.replace(/[^0-9.,]/g, ''))}
            />
            {unit === 'percent' && <span>%</span>}
        </span>
    );
}

function AlertsConfigurationClass({t}) {
    const [alerts, setAlerts] = useState(INITIAL_ALERTS);
    const [lastId, setLastId] = useState(INITIAL_ALERTS.length);
    const [hasDraftChanges, setHasDraftChanges] = useState(false);

    const selectableOptions = useMemo(() => ({
        channels: CHANNELS.map(option => ({...option, label: optionLabel(option, t)})),
        recipients: RECIPIENTS.map(option => ({...option, label: optionLabel(option, t)})),
        dimensions: DIMENSIONS.map(option => ({...option, label: optionLabel(option, t)})),
        alertTypes: ALERT_TYPES.map(option => ({...option, label: optionLabel(option, t)})),
        metrics: METRICS.map(option => ({...option, label: optionLabel(option, t)})),
        comparators: COMPARATORS.map(option => ({...option, label: optionLabel(option, t)})),
    }), [t]);

    const updateAlert = (id, patch) => {
        setAlerts(prev => prev.map(alert => alert.id === id ? {...alert, ...patch} : alert));
        setHasDraftChanges(true);
    };

    const addAlert = () => {
        const nextId = lastId + 1;
        setLastId(nextId);
        setAlerts(prev => ([
            ...prev,
            {
                id: nextId,
                active: true,
                name: '',
                channel: 'mail',
                recipient: 'me',
                dimension: 'client',
                dimensionValue: '',
                type: 'critical_metric',
                metric: 'roas',
                comparator: 'lt',
                value: '',
            },
        ]));
        setHasDraftChanges(true);
    };

    const deleteAlert = (id) => {
        setAlerts(prev => prev.filter(alert => alert.id !== id));
        setHasDraftChanges(true);
    };

    const saveDraftChanges = () => {
        setHasDraftChanges(false);
    };

    const renderDimensionValue = (alert) => {
        const dimension = findOption(DIMENSIONS, alert.dimension);
        if (!dimension.sublist) return null;

        const options = (SUBLISTS[dimension.sublist] || []).map(option => ({...option, label: optionLabel(option, t)}));

        return (
            <SelectChip
                value={alert.dimensionValue}
                options={options}
                placeholder={t('alerts_configuration_builder.choose_dimension_value')}
                accent={Boolean(alert.dimensionValue)}
                onChange={(value) => updateAlert(alert.id, {dimensionValue: value})}
            />
        );
    };

    const renderMetricCondition = (alert) => {
        const type = findOption(ALERT_TYPES, alert.type);
        if (!type.metric) {
            return <span className="alert-builder-word">.</span>;
        }

        return (
            <>
                <span className="alert-builder-word">:</span>
                <SelectChip
                    value={alert.metric}
                    options={selectableOptions.metrics}
                    onChange={(value) => updateAlert(alert.id, {metric: value})}
                />
                <span className="alert-builder-word">{t('alerts_configuration_builder.word_is')}</span>
                <SelectChip
                    value={alert.comparator}
                    options={selectableOptions.comparators}
                    onChange={(value) => updateAlert(alert.id, {comparator: value})}
                />
                <ValueInput
                    metric={alert.metric}
                    value={alert.value}
                    onChange={(value) => updateAlert(alert.id, {value})}
                />
            </>
        );
    };

    const renderAlert = (alert, index) => {
        const channel = findOption(CHANNELS, alert.channel);
        const channelLabel = optionLabel(channel, t);

        return (
            <article key={alert.id} className={`alert-builder-card${alert.active ? ' -active' : ' -muted'}`}>
                <div className="alert-builder-card-top">
                    <div className="alert-builder-id">
                        <span className="alert-builder-index">{String(index + 1).padStart(2, '0')}</span>
                        <input
                            type="text"
                            value={alert.name}
                            placeholder={t('alerts_configuration_builder.name_placeholder')}
                            onChange={(e) => updateAlert(alert.id, {name: e.target.value})}
                        />
                        <span className={`alert-builder-channel ${channel.className}`}>{channelLabel}</span>
                    </div>
                    <div className="alert-builder-tools">
                        <span className={`alert-builder-status${alert.active ? ' -active' : ''}`}>
                            {alert.active ? t('alerts_configuration_builder.active') : t('alerts_configuration_builder.paused')}
                        </span>
                        <Switch
                            checked={alert.active}
                            onChange={(event) => updateAlert(alert.id, {active: event.target.checked})}
                            style={{margin: 0}}
                        />
                        <button
                            type="button"
                            className="alert-builder-delete"
                            onClick={() => deleteAlert(alert.id)}
                            aria-label={t('alerts_configuration_builder.delete_alert')}
                            title={t('alerts_configuration_builder.delete_alert')}
                        >
                            <Trash size={15}/>
                        </button>
                    </div>
                </div>

                <div className="alert-builder-sentence">
                    <span className="alert-builder-word">{t('alerts_configuration_builder.word_notify_by')}</span>
                    <SelectChip
                        value={alert.channel}
                        options={selectableOptions.channels}
                        onChange={(value) => updateAlert(alert.id, {channel: value})}
                    />
                    <SelectChip
                        value={alert.recipient}
                        options={selectableOptions.recipients}
                        onChange={(value) => updateAlert(alert.id, {recipient: value})}
                    />
                    <span className="alert-builder-word">{t('alerts_configuration_builder.word_when')}</span>
                    <SelectChip
                        value={alert.dimension}
                        options={selectableOptions.dimensions}
                        onChange={(value) => updateAlert(alert.id, {dimension: value, dimensionValue: ''})}
                    />
                    {renderDimensionValue(alert)}
                    <span className="alert-builder-word">{t('alerts_configuration_builder.word_has')}</span>
                    <SelectChip
                        value={alert.type}
                        options={selectableOptions.alertTypes}
                        onChange={(value) => updateAlert(alert.id, {type: value})}
                    />
                    {renderMetricCondition(alert)}
                </div>
            </article>
        );
    };

    return (
        <div className="alerts-configuration-builder">
            <div className="alerts-builder-toolbar">
                <div className="alerts-builder-summary">
                    <strong>{alerts.length}</strong>
                    <span>{t('alerts_configuration_builder.alerts_count')}</span>
                    <span className="alerts-builder-summary-dot -active"/>
                    <span>{alerts.filter(alert => alert.active).length} {t('alerts_configuration_builder.active_count')}</span>
                    <span className="alerts-builder-summary-dot"/>
                    <span>{alerts.filter(alert => !alert.active).length} {t('alerts_configuration_builder.paused_count')}</span>
                </div>
                <button type="button" className="btn -alt alerts-builder-new-button" onClick={addAlert}>
                    {t('alerts_configuration_builder.new_alert')}
                </button>
            </div>

            <div className="alerts-builder-list">
                {alerts.length > 0
                    ? alerts.map(renderAlert)
                    : (
                        <div className="alerts-builder-empty">
                            <strong>{t('alerts_configuration_builder.empty_title')}</strong>
                            <span>{t('alerts_configuration_builder.empty_description')}</span>
                        </div>
                    )}
            </div>

            <div className="alerts-builder-save-row">
                <button
                    type="button"
                    className="btn -alt"
                    onClick={saveDraftChanges}
                    disabled={!hasDraftChanges}
                >
                    {hasDraftChanges
                        ? t('alerts_configuration_builder.save_changes')
                        : t('alerts_configuration_builder.saved')}
                </button>
            </div>
        </div>
    );
}

export const AlertsConfiguration = withTranslation()(AlertsConfigurationClass);
export default AlertsConfiguration;
